import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import CustomTextField from "../CustomTextField";
import CustomDialog from "../CustomDialog";
import ConfirmDialog from "../ConfirmDialog";
import {
  closeMatch,
  deleteMatch,
  getMatch,
  Match,
  MATCH_DESCRIPTION_MAX,
  MatchDetails,
  MatchLevel,
  MatchLevelsStatus,
  MatchMode,
  MatchParticipant,
  MatchUpdatePayload,
  removeMatchPlayer,
  updateMatch,
} from "../../services/MatchmakingService";
import { formatTableDateTime } from "../../utils/tableDateFormat";
import {
  BookingStateBadge,
  courtLabel,
  formatSlot,
  ModeBadge,
  RequestStatusBadge,
  StatusBadge,
} from "./matchmakingUi";

interface MatchDetailsDrawerProps {
  /** The list row, shown straight away while the full details load. */
  match: Match | null;
  levels: MatchLevel[];
  levelsStatus: MatchLevelsStatus;
  onClose: () => void;
  onUpdated: (match: Match) => void;
  onDeleted: (matchId: string) => void;
}

type PendingConfirm =
  | { kind: "remove"; participant: MatchParticipant }
  | { kind: "delete" }
  | null;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: COLORS.placeHolderColor,
        mb: 1,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.5, alignItems: "flex-start" }}>
    <Typography
      sx={{ width: 110, flexShrink: 0, fontSize: "0.85rem", color: COLORS.placeHolderColor }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: "0.88rem", minWidth: 0, wordBreak: "break-word" }}>
      {children}
    </Box>
  </Box>
);

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="span" sx={{ fontSize: "0.88rem", color: COLORS.placeHolderColor }}>
    {children}
  </Typography>
);

const MatchDetailsDrawer: React.FC<MatchDetailsDrawerProps> = ({
  match,
  levels,
  levelsStatus,
  onClose,
  onUpdated,
  onDeleted,
}) => {
  const matchId = match?.id ?? null;
  const [details, setDetails] = useState<MatchDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<PendingConfirm>(null);
  const latestRequest = useRef(0);

  useEffect(() => {
    setDetails(null);
    setEditOpen(false);
    setConfirm(null);
    if (!matchId) return;

    const requestId = ++latestRequest.current;
    setLoading(true);
    getMatch(matchId)
      .then((result) => {
        if (requestId === latestRequest.current) setDetails(result);
      })
      .catch(() => {
        // The interceptor already surfaced the message.
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [matchId]);

  // Keeps the content in place while the drawer slides shut.
  const lastMatch = useRef<Match | null>(null);
  if (match) lastMatch.current = match;

  // Until the details arrive, render from the list row (it has everything but requests).
  const view: Match | null = details || match || lastMatch.current;

  const participants = useMemo(
    () =>
      (view?.participants || [])
        .slice()
        .sort((a, b) => (a.role === "HOST" ? -1 : b.role === "HOST" ? 1 : 0)),
    [view]
  );

  const applyUpdate = (updated: MatchDetails) => {
    setDetails(updated);
    onUpdated(updated);
  };

  const runAction = async (action: () => Promise<MatchDetails>, success: string) => {
    setBusy(true);
    try {
      applyUpdate(await action());
      toast.success(success);
    } catch {
      // The interceptor already surfaced the backend message.
    } finally {
      setBusy(false);
    }
  };

  // Quiet refetch for when an edit was refused because the match moved on.
  const reloadDetails = async () => {
    if (!view) return;
    try {
      applyUpdate(await getMatch(view.id));
    } catch {
      // The interceptor already surfaced the message.
    }
  };

  const handleClose = () => {
    if (!view) return;
    runAction(() => closeMatch(view.id), "Match closed.");
  };

  const handleConfirm = async () => {
    const pending = confirm;
    setConfirm(null);
    if (!pending || !view) return;

    if (pending.kind === "remove") {
      runAction(
        () => removeMatchPlayer(view.id, pending.participant.id),
        `${pending.participant.name || "Player"} removed from the match.`
      );
      return;
    }

    setBusy(true);
    try {
      await deleteMatch(view.id);
      toast.success("Match deleted.");
      onDeleted(view.id);
    } catch {
      // The interceptor already surfaced the backend message.
    } finally {
      setBusy(false);
    }
  };

  const openSpots = view ? Math.max(0, view.targetPlayers - view.currentPlayers) : 0;
  const matchJoinedCount = participants.filter((p) => p.joinedVia === "MATCH").length;
  const hostName = view?.owner?.full_name || "the host";

  const removeBlockedReason = view?.bookingCancelled
    ? "The booking is cancelled."
    : view?.bookingPaid
    ? "Cannot remove players after payment."
    : "";
  const deleteBlockedReason = view?.bookingPaid
    ? "Cannot delete a match after the booking is paid."
    : "";

  return (
    <Drawer
      anchor="right"
      open={!!match}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 540 } } }}
    >
      {/* The app bar sits above drawers; keep the header clear of it. */}
      <Toolbar />
      {view && (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 2,
              borderBottom: `1px solid ${COLORS.BorderColor}`,
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.15rem" }}>
                Match details
              </Typography>
              <Box sx={{ display: "flex", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
                <StatusBadge status={view.status} />
                <ModeBadge mode={view.mode} />
                <BookingStateBadge match={view} />
              </Box>
            </Box>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", gap: 1, px: 2.5, py: 1.5, flexWrap: "wrap" }}>
            <CustomButton
              label="Edit details"
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={() => setEditOpen(true)}
            />
            {view.status !== "CLOSED" && (
              <CustomButton
                label="Close match"
                variant="outlined"
                size="small"
                disabled={busy}
                onClick={handleClose}
              />
            )}
            <Tooltip title={deleteBlockedReason}>
              <span>
                <CustomButton
                  label="Delete match"
                  variant="outlined"
                  size="small"
                  textColor={COLORS.RED}
                  borderColor={COLORS.RED}
                  disabled={busy || !!deleteBlockedReason}
                  onClick={() => setConfirm({ kind: "delete" })}
                />
              </span>
            </Tooltip>
            {busy && <CircularProgress size={20} sx={{ alignSelf: "center" }} />}
          </Box>

          <Divider />

          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2.5, py: 2 }}>
            <Section title="Booking">
              <InfoRow label="When">
                {formatSlot(view.appointment?.start_at, view.appointment?.end_at)}
              </InfoRow>
              <InfoRow label="Venue · court">{courtLabel(view)}</InfoRow>
              <InfoRow label="Service">
                {view.appointment?.service?.name || <Muted>—</Muted>}
              </InfoRow>
              <InfoRow label="Host">
                {view.owner?.full_name || "-"}
                {view.owner?.phone && <Muted> · {view.owner.phone}</Muted>}
              </InfoRow>
              <InfoRow label="Players">
                {view.currentPlayers}/{view.targetPlayers}
                {openSpots > 0 && (
                  <Muted> · {openSpots} {openSpots === 1 ? "spot" : "spots"} open</Muted>
                )}
              </InfoRow>
              <InfoRow label="Players needed">{view.players_needed}</InfoRow>
              <InfoRow label="Total price">
                {view.appointment?.total_price ?? <Muted>—</Muted>}
              </InfoRow>
            </Section>

            <Section title="Match">
              <InfoRow label="Level">
                {view.level || <Muted>No level</Muted>}
              </InfoRow>
              <InfoRow label="Visibility">
                <ModeBadge mode={view.mode} />
              </InfoRow>
              <InfoRow label="Status">
                <StatusBadge status={view.status} />
              </InfoRow>
              <InfoRow label="Description">
                {view.description ? (
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {view.description}
                  </Box>
                ) : (
                  <Muted>No description</Muted>
                )}
              </InfoRow>
              {view.note && <InfoRow label="Note">{view.note}</InfoRow>}
            </Section>

            <Section title={`Participants (${participants.length})`}>
              {participants.length === 0 && <Muted>No participants.</Muted>}
              {participants.map((participant) => (
                <Box
                  key={participant.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 1,
                    borderBottom: `1px solid ${COLORS.BorderColor}`,
                  }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        {participant.name || "Unnamed player"}
                      </Typography>
                      {participant.role === "HOST" && (
                        <Chip size="small" label="Host" sx={{ height: 20, fontSize: "0.7rem" }} />
                      )}
                      {participant.joinedVia === "INVITE" && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="via invite link"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: "0.78rem", color: COLORS.placeHolderColor }}>
                      {[participant.phone, participant.email].filter(Boolean).join(" · ") || "—"}
                      {participant.joinedAt &&
                        ` · joined ${formatTableDateTime(participant.joinedAt)}`}
                    </Typography>
                  </Box>
                  {participant.role !== "HOST" && (
                    <Tooltip title={removeBlockedReason || "Remove player"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy || !!removeBlockedReason}
                          onClick={() => setConfirm({ kind: "remove", participant })}
                          aria-label={`Remove ${participant.name}`}
                        >
                          <PersonRemoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Section>

            <Section title="Request history">
              {!details && loading && <CircularProgress size={22} />}
              {details && details.requests.length === 0 && (
                <Muted>No requests yet.</Muted>
              )}
              {details?.requests.map((request) => (
                <Box
                  key={request.id}
                  sx={{ py: 1, borderBottom: `1px solid ${COLORS.BorderColor}` }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", flexGrow: 1 }}>
                      {request.requester?.name || "Unknown player"}
                    </Typography>
                    <RequestStatusBadge status={request.status} />
                  </Box>
                  <Typography sx={{ fontSize: "0.78rem", color: COLORS.placeHolderColor }}>
                    {[request.requester?.phone, request.requester?.email]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </Typography>
                  {request.message && (
                    <Typography sx={{ fontSize: "0.85rem", mt: 0.5, whiteSpace: "pre-wrap" }}>
                      “{request.message}”
                    </Typography>
                  )}
                  {request.responseReason && (
                    <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
                      <Muted>Reason: </Muted>
                      {request.responseReason}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "0.75rem", color: COLORS.placeHolderColor, mt: 0.5 }}>
                    Requested {formatTableDateTime(request.created_at)}
                    {request.responded_at &&
                      ` · answered ${formatTableDateTime(request.responded_at)}`}
                  </Typography>
                </Box>
              ))}
            </Section>
          </Box>
        </Box>
      )}

      {view && editOpen && (
        <MatchEditDialog
          match={view}
          levels={levels}
          levelsStatus={levelsStatus}
          onClose={() => setEditOpen(false)}
          onStale={reloadDetails}
          onSaved={(updated) => {
            applyUpdate(updated);
            setEditOpen(false);
            toast.success("Match updated.");
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        title={confirm?.kind === "delete" ? "Delete match" : "Remove player"}
        confirmButtonText={confirm?.kind === "delete" ? "Delete" : "Remove"}
        message={
          confirm?.kind === "delete"
            ? `Delete this match? The booking stays with ${hostName}. ${matchJoinedCount} player(s) who joined through the match will be removed and notified.`
            : confirm?.kind === "remove"
            ? `Remove ${confirm.participant.name || "this player"} from the match? They will be notified.`
            : ""
        }
      />
    </Drawer>
  );
};

/* ------------------------------------------------------------ edit dialog */

const NO_LEVEL = "";

interface MatchEditDialogProps {
  match: Match;
  levels: MatchLevel[];
  levelsStatus: MatchLevelsStatus;
  onClose: () => void;
  onSaved: (match: MatchDetails) => void;
  /** The backend refused playersNeeded; the match may have changed meanwhile. */
  onStale: () => void;
}

// Mounted only while open, so the form starts from the match every time and a
// parent re-render can't reset what the admin is typing.
const MatchEditDialog: React.FC<MatchEditDialogProps> = ({
  match,
  levels,
  levelsStatus,
  onClose,
  onSaved,
  onStale,
}) => {
  const [description, setDescription] = useState(match.description || "");
  const [level, setLevel] = useState(match.level || NO_LEVEL);
  const [mode, setMode] = useState<MatchMode>(match.mode);
  const [playersNeeded, setPlayersNeeded] = useState(String(match.players_needed ?? ""));
  const [saving, setSaving] = useState(false);

  const levelsLoaded = levelsStatus === "loaded";

  // Props, not state: after a refused save the drawer refetches and these move.
  const minNeeded = Math.max(1, Number(match.minPlayersNeeded) || 1);
  const maxNeeded = Number(match.maxPlayersNeeded) || Infinity;
  const neededValue = Number(playersNeeded);
  const neededValid =
    playersNeeded !== "" &&
    Number.isInteger(neededValue) &&
    neededValue >= minNeeded &&
    neededValue <= maxNeeded;
  const neededError = neededValid
    ? ""
    : Number.isFinite(maxNeeded)
    ? `Must be between ${minNeeded} and ${maxNeeded}.`
    : `Must be at least ${minNeeded}.`;
  // The target moves one-for-one with players_needed (the host is the rest).
  const targetPlayers = neededValid
    ? match.targetPlayers + (neededValue - match.players_needed)
    : match.targetPlayers;
  const neededHint =
    neededValid && neededValue === minNeeded && minNeeded > 1
      ? `Can't go lower: ${match.currentPlayers} players have already joined.`
      : `${match.currentPlayers} of ${targetPlayers} players in`;

  const stepNeeded = (delta: number) => {
    const base = neededValid ? neededValue : match.players_needed;
    setPlayersNeeded(String(Math.min(maxNeeded, Math.max(minNeeded, base + delta))));
  };

  // A level deleted from the catalogue stays on existing matches. Offer it as
  // the current value so saving doesn't quietly swap it for something else.
  // Without a loaded list we can't tell, so show it plain and lock the select.
  const orphanLevel =
    match.level && !levels.some((option) => option.name === match.level)
      ? match.level
      : null;

  const buildPayload = (): MatchUpdatePayload => {
    const payload: MatchUpdatePayload = {};
    // Compare the untouched text, so a stored description with stray
    // whitespace isn't sent back just because the form trims it.
    if (description !== (match.description || "")) {
      payload.description = description.trim() || null;
    }
    const nextLevel = level || null;
    if (nextLevel !== (match.level || null)) payload.level = nextLevel;
    if (mode !== match.mode) payload.mode = mode;
    if (neededValue !== match.players_needed) payload.playersNeeded = neededValue;
    return payload;
  };

  const handleSave = async () => {
    if (!neededValid) return;
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      onSaved(await updateMatch(match.id, payload));
    } catch (error: any) {
      // The interceptor toasts the 400. For playersNeeded it can mean a player
      // joined meanwhile, so pull the match again for fresh limits.
      if (error?.response?.status === 400 && payload.playersNeeded !== undefined) {
        onStale();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog
      open
      onClose={onClose}
      title="Edit match"
      actions={
        <>
          <CustomButton label="Cancel" onClick={onClose} disabled={saving} />
          <CustomButton
            label="Save"
            variant="contained"
            loading={saving}
            disabled={!neededValid}
            onClick={handleSave}
          />
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.75 }}>
            Level
          </Typography>
          <CustomSelect
            fullWidth
            placeholder="No level"
            disabled={!levelsLoaded}
            value={level}
            onChange={(value) => setLevel(String(value ?? NO_LEVEL))}
            options={[
              { label: "No level", value: NO_LEVEL },
              ...(orphanLevel
                ? [
                    {
                      label: levelsLoaded ? `${orphanLevel} (removed)` : orphanLevel,
                      value: orphanLevel,
                    },
                  ]
                : []),
              ...levels.map((option) => ({ label: option.name, value: option.name })),
            ]}
          />
          {!levelsLoaded && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                mt: 0.5,
                color: levelsStatus === "failed" ? COLORS.RED : COLORS.placeHolderColor,
              }}
            >
              {levelsStatus === "failed" ? "Couldn't load levels" : "Loading levels…"}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.75 }}>
            Visibility
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, next: MatchMode | null) => next && setMode(next)}
          >
            <ToggleButton value="PUBLIC" sx={{ textTransform: "none", px: 2 }}>
              Public
            </ToggleButton>
            <ToggleButton value="PRIVATE" sx={{ textTransform: "none", px: 2 }}>
              Private
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.75 }}>
            Players needed
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              aria-label="Fewer players needed"
              disabled={neededValid && neededValue <= minNeeded}
              onClick={() => stepNeeded(-1)}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Box sx={{ width: 88 }}>
              <CustomTextField
                type="number"
                minWidth="0px"
                value={playersNeeded}
                error={!neededValid}
                onChange={(e) => setPlayersNeeded(e.target.value.replace(/[^0-9]/g, ""))}
                inputProps={{
                  min: minNeeded,
                  max: Number.isFinite(maxNeeded) ? maxNeeded : undefined,
                  step: 1,
                  inputMode: "numeric",
                  "aria-label": "Players needed",
                  style: { textAlign: "center" },
                }}
              />
            </Box>
            <IconButton
              size="small"
              aria-label="More players needed"
              disabled={neededValid && neededValue >= maxNeeded}
              onClick={() => stepNeeded(1)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              mt: 0.5,
              color: neededValid ? COLORS.placeHolderColor : COLORS.RED,
            }}
          >
            {neededValid ? neededHint : neededError}
          </Typography>
        </Box>

        <CustomTextField
          label="Description"
          multiline
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, MATCH_DESCRIPTION_MAX))}
          inputProps={{ maxLength: MATCH_DESCRIPTION_MAX }}
          helperText={`${description.length}/${MATCH_DESCRIPTION_MAX} · leave empty to clear`}
        />
      </Box>
    </CustomDialog>
  );
};

export default MatchDetailsDrawer;
